import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { User } from '@/types/user';

interface UseUsersOptions {
  enabled?: boolean;
  role?: string;
  active?: boolean;
}

export function useUsers(options: UseUsersOptions = {}) {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { enabled = true, role, active = true } = options;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    loadUsers();
  }, [enabled, role, active]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get current user's company
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // profiles.id IS the user_id (references auth.users.id)
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', currentUser.id)
        .single();

      if (!currentProfile) {
        throw new Error('User profile not found');
      }

      // Build query for users in the same company
      // profiles.id IS the user_id (references auth.users.id)
      let query = supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          username,
          company_id,
          is_admin,
          updated_at
        `)
        .eq('company_id', currentProfile.company_id)
        .order('full_name');

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      // Transform to User interface
      // Note: profiles.id is the user's UUID (same as auth.users.id)
      const transformedUsers: User[] = (data || []).map(profile => ({
        id: profile.id,
        name: profile.full_name || profile.username || 'Usuário',
        email: '', // Email is in auth.users, not accessible via profiles
        role: profile.is_admin ? 'admin' : 'user',
        is_active: true, // profiles doesn't have is_active, assume all are active
        created_at: profile.updated_at || new Date().toISOString(),
        updated_at: profile.updated_at || new Date().toISOString(),
      }));

      setUsers(transformedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err : new Error('Failed to load users'));
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = () => loadUsers();

  return {
    users,
    isLoading,
    error,
    refetch,
  };
}